import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { Tag, Empty, Spin, Popconfirm } from 'antd';
import { ReloadOutlined, SafetyCertificateOutlined, CameraOutlined, WarningOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import config from '../../config';
import './pageSentinelle.css';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;

/* ---------------------------------------------------------------------------
   Sentinelle d'intégrité (méthodes A+B, 15/07) — vue admin du rapport :
   - écarts stock : réel vs (ligne de base + mouvements + réceptions) ;
   - écritures DIRECTES en base (changement de stock sans mouvement corrélé),
     détectées par les triggers avec le compte MySQL ;
   - créances divergentes vs le calcul canonique.
   Les alertes automatiques (cloche + push) tournent côté serveur chaque heure ;
   cette page sert à VOIR le détail et à reposer la ligne de base après un
   inventaire validé.
--------------------------------------------------------------------------- */

const money = (n) => `${Number(n || 0).toLocaleString('fr-FR')} $`;
const quand = (d) => { try { return format(new Date(d), 'dd/MM/yyyy HH:mm'); } catch (e) { return '—'; } };

const PageSentinelle = () => {
  const role = useSelector((s) => s.user?.currentUser?.role);
  const [rapport, setRapport] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [reposeEnCours, setReposeEnCours] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await axios.get(`${DOMAIN}/api/sentinelle/rapport`);
      setRapport(data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Rapport indisponible.');
    } finally { setChargement(false); }
  }, []);

  useEffect(() => { if (role === 'admin') charger(); }, [role, charger]);

  const reposerBaseline = async () => {
    setReposeEnCours(true);
    try {
      const { data } = await axios.post(`${DOMAIN}/api/sentinelle/baseline`);
      toast.success(`Ligne de base reposée : ${data.variantes} variantes photographiées.`);
      await charger();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Repose impossible.');
    } finally { setReposeEnCours(false); }
  };

  if (role !== 'admin') {
    return (
      <div className="sent-refus">
        <h2>Sentinelle d'intégrité</h2>
        <p>Page réservée à l'administrateur.</p>
      </div>
    );
  }

  const totalAnomalies = rapport
    ? (rapport.stock?.length || 0) + (rapport.directes?.length || 0) + (rapport.creances?.length || 0)
    : 0;

  return (
    <div className="sent-page">
      <div className="sent-entete">
        <h1><SafetyCertificateOutlined /> Sentinelle d'intégrité</h1>
        <div className="sent-actions">
          <button type="button" onClick={charger} disabled={chargement}><ReloadOutlined /> Actualiser</button>
          <Popconfirm
            title="Reposer la ligne de base ?"
            description={<span>Le stock ACTUEL de chaque variante devient la nouvelle référence.<br />
              À faire uniquement après un inventaire validé ou une correction assumée —<br />
              tout écart non résolu sera considéré comme « normal » à partir de maintenant.</span>}
            onConfirm={reposerBaseline} okText="Reposer" cancelText="Annuler">
            <button type="button" disabled={reposeEnCours}><CameraOutlined /> Reposer la ligne de base</button>
          </Popconfirm>
        </div>
      </div>

      {chargement && !rapport && <div className="sent-centre"><Spin /></div>}

      {rapport && (
        <>
          <div className="sent-cartes">
            <div className={`sent-carte${totalAnomalies ? ' alerte' : ' ok'}`}>
              <small>État</small>
              <b>{totalAnomalies ? `⚠ ${totalAnomalies} anomalie(s)` : '✅ Tout est cohérent'}</b>
            </div>
            <div className="sent-carte">
              <small>Ligne de base</small>
              <b>{rapport.baseline?.posee ? `${rapport.baseline.variantes} variantes` : 'Non posée'}</b>
              {rapport.baseline?.depuis && <span>depuis le {quand(rapport.baseline.depuis)}</span>}
            </div>
            <div className={`sent-carte${rapport.stock?.length ? ' alerte' : ''}`}>
              <small>Écarts de stock</small><b>{rapport.stock?.length || 0}</b>
            </div>
            <div className={`sent-carte${rapport.directes?.length ? ' alerte' : ''}`}>
              <small>Écritures directes (26 h)</small><b>{rapport.directes?.length || 0}</b>
            </div>
            <div className={`sent-carte${rapport.creances?.length ? ' alerte' : ''}`}>
              <small>Créances divergentes</small><b>{rapport.creances?.length || 0}</b>
            </div>
          </div>

          <section className="sent-section">
            <h2><WarningOutlined /> Écarts de stock <small>réel ≠ référence + mouvements + réceptions</small></h2>
            {!rapport.stock?.length ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucun écart 🎉" /> : (
              <div className="sent-table-wrap"><table>
                <thead><tr><th>Variante</th><th>Référence</th><th>Attendu</th><th>Réel</th><th>Écart</th><th>Référence posée le</th></tr></thead>
                <tbody>
                  {rapport.stock.map((x) => (
                    <tr key={x.id_varianteProduit}>
                      <td>{x.nom_produit || `Variante ${x.id_varianteProduit}`}{x.taille ? ` · P${x.taille}` : ''}</td>
                      <td>{x.stock_reference}</td>
                      <td>{x.stock_theorique}</td>
                      <td><b>{x.stock_reel}</b></td>
                      <td><Tag color="red">{Number(x.stock_reel) - Number(x.stock_theorique) > 0 ? '+' : ''}{Number(x.stock_reel) - Number(x.stock_theorique)}</Tag></td>
                      <td>{quand(x.date_reference)}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </section>

          <section className="sent-section">
            <h2><WarningOutlined /> Écritures directes en base <small>changement de stock SANS mouvement ni réception — détecté par les triggers</small></h2>
            {!rapport.directes?.length ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucune écriture directe détectée 🎉" /> : (
              <div className="sent-table-wrap"><table>
                <thead><tr><th>Quand</th><th>Variante</th><th>Avant</th><th>Après</th><th>Compte MySQL</th></tr></thead>
                <tbody>
                  {rapport.directes.map((x) => (
                    <tr key={x.id}>
                      <td>{quand(x.date_audit)}</td>
                      <td>{x.nom_produit || `Variante ${x.id_varianteProduit}`}{x.taille ? ` · P${x.taille}` : ''}</td>
                      <td>{x.avant}</td>
                      <td><b>{x.apres}</b></td>
                      <td><Tag color="volcano">{x.utilisateur_mysql}</Tag></td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </section>

          <section className="sent-section">
            <h2><WarningOutlined /> Créances divergentes <small>montant en base ≠ calcul depuis les ventes (net des retours)</small></h2>
            {!rapport.creances?.length ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Toutes les créances collent au calcul 🎉" /> : (
              <div className="sent-table-wrap"><table>
                <thead><tr><th>Commande</th><th>Cliente</th><th>En base</th><th>Calcul canonique</th><th>Écart</th></tr></thead>
                <tbody>
                  {/* clé composée : la table dette peut porter des DOUBLONS par
                      commande (anomalie que cette page sert justement à montrer) */}
                  {rapport.creances.map((x, i) => (
                    <tr key={`${x.id_commande}-${i}`}>
                      <td>#{x.id_commande}</td>
                      <td>{x.client || '—'}</td>
                      <td>{money(x.stocke)}</td>
                      <td>{money(x.canonique)}</td>
                      <td><Tag color="orange">{money(Number(x.stocke || 0) - Number(x.canonique || 0))}</Tag></td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </section>

          <p className="sent-note">
            La sentinelle repasse automatiquement chaque heure et t'alerte (cloche + notification téléphone)
            dès que la situation change. Après un inventaire validé, pense à « Reposer la ligne de base ».
          </p>
        </>
      )}
    </div>
  );
};

export default PageSentinelle;
